import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { api } from '../../services/api';
import { ChatApiChannel } from '../../services/chat/chatApiChannel';
import { useChatThreadSource } from '../../hooks/useChatThreadSource';
import { useAiDataConsent } from '../../hooks/useAiDataConsent';
import { useI18n } from '../../i18n';
import { AiDataConsent } from './AiDataConsent';
import { AiConsentDeclined } from './AiConsentDeclined';
import { AiNoticeLink } from './AiNoticeLink';
import { AgapellaChatThread } from './AgapellaChatThread';
import { useMessagesSurface } from './messagesSurface';
import { BehaviorEvents, trackBehavior, interest } from '../../services/behaviorAnalytics';
import { aiChatSurface, emitProductEvent, ProductEvents } from '../../services/productEvents';
import { DEFAULT_ASSISTANT_ID } from '../../constants/chat';
import { AGAPELLA_AI_ICON } from '../../constants/aiCompanions';
import type { ComposerUpload } from '../../services/chat/streamUpload';
import { downscaleImage } from '../../services/chat/downscaleImage';

/**
 * CORE-371 / CORE-396 — drives a 1:1 AI companion or the Wisdom Council over
 * Agape.ChatApi. Just wires a ChatApiChannel source into the backend-agnostic
 * AgapellaChatThread via useChatThreadSource; all state + UI live there. Rendered
 * by ChatsScreen for `entityType` `ai` / `wisdom-council` when the
 * `ai_chat_via_chat_api` flag is on.
 */
interface Props {
  entityType: 'ai' | 'wisdom-council';
  /** Companion id for `ai` (e.g. `sophia`); ignored for the council. */
  companionId: string;
  name: string;
  avatarUrl?: string | null;
  /** Current Stream user id (e.g. `user_<bareId>`); ChatApi uses the bare id. */
  myUserId: string;
  /** Companions offered by @-mention autocomplete (the council members). */
  mentionables?: { id: string; name: string }[];
  /** Mobile-only: back to the conversation list (this panel has no header). */
  onBack?: () => void;
  /** Content pinned above the message list (e.g. the CORE-501 replay CTA). */
  headerSlot?: React.ReactNode;
  /**
   * CORE-1356 — вложения в разговоре с Агапеллой (веб). Без него композер
   * текстовый, как и был.
   */
  onUpload?: ComposerUpload;
  /** `accept` для выбора файла — из лимитов канала. */
  acceptFileTypes?: string;
}

export const ChatApiCompanionPanel: React.FC<Props> = ({ entityType, companionId, name, avatarUrl, myUserId, mentionables, onBack, headerSlot, onUpload, acceptFileTypes }) => {
  const bareId = (myUserId || '').replace(/^user_/, '');

  // CORE-896 — единственная точка, через которую открывается ИИ-чат (и полный
  // экран, и виджет), поэтому гейт согласия стоит здесь: новый вход в чат не
  // сможет обойти его, забыв продублировать проверку у себя.
  const { i18n } = useI18n();
  const { status: consentStatus, accept, revoke } = useAiDataConsent(bareId);
  const consented = consentStatus === 'granted';
  // CORE-1401, возврат QA 23.10 — «Не сейчас» без экрана, куда вернуться.
  // На телефоне кнопка уводит к списку чатов (`onBack`), а на десктопе и в
  // виджете `onBack` нет — и нажатие не меняло на экране ничего: тот же гейт
  // на том же месте. Здесь отказ получает свой видимый итог.
  const [declined, setDeclined] = useState(false);

  // Both channels are provisioned on demand (idempotent): the council key is
  // deterministic; a 1:1 companion is auto-id'd on Stream, so the backend mints
  // `ai-<bareId>-<companionId>`.
  const makeSource = useCallback(async () => {
    const { channelKey } =
      entityType === 'wisdom-council'
        ? await api.ensureWisdomCouncilChannel()
        : await api.ensureAiCompanionChannel(companionId);
    return new ChatApiChannel(channelKey, bareId);
  }, [entityType, companionId, bareId]);

  // До согласия канал даже не заводим: `null` держит поток бездействующим, и
  // ни одного обращения к ИИ-стороне из этого экрана не уходит.
  // CORE-1791 — в /chats на телефоне тред живёт и под списком; прочитанное —
  // только на экране. В виджете поверхности нет, и всё как было.
  const threadShown = useMessagesSurface()?.threadShown ?? true;
  const { messages, thinking, loading, error, send, react, notifyTyping } =
    useChatThreadSource(consented ? makeSource : null, { shown: threadShown });

  // CORE-1356 — картинка ужимается ровно здесь, между выбором файла и
  // загрузчиком: он общий с кругами и личными чатами, а платит за пиксели
  // только разговор с ИИ. Ужатие мягкое — не смогли, отправляем как есть.
  const aiUpload = useCallback<ComposerUpload>(
    async (file, meta) => {
      if (!onUpload) return null;
      return onUpload(await downscaleImage(file), meta);
    },
    [onUpload],
  );

  // CORE-497 — Agapella chat was entirely un-instrumented. Emit an "opened"
  // signal once per companion open, and a message signal on each send (with
  // first-message detection). companionId is in scope here (the composer in
  // AgapellaChatThread is backend-agnostic and has none), so we wrap `send`.
  // CORE-1753 — имя поверхности общее с нативом: событие теперь шлют оба.
  const surface = aiChatSurface(entityType, companionId);
  useEffect(() => {
    // CORE-896 — экран согласия вместо чата открытием чата не является:
    // считать его так значило бы завышать метрику ровно на тех, кто до чата
    // не дошёл.
    if (!consented) return;
    // CORE-1791 — на телефоне тред больше не размонтируется при возврате к
    // списку. Раньше каждое новое открытие чата было новым монтированием и
    // слало событие; теперь «открытие» — это показ, иначе повторный вход в чат
    // перестал бы считаться (и не будил бы приветствие при возвращении, CORE-1753).
    if (!threadShown) return;
    trackBehavior(BehaviorEvents.agapella.chatOpened, {
      surface,
      interest: interest.activity('agapella_chat'),
    });
    // Product-event beacon (awesome-chat-api sink): a chat open is a client-only
    // signal the backend can't see. Fire-and-forget, separate from trackBehavior.
    emitProductEvent(ProductEvents.chatOpened, { userId: bareId, props: { surface } });
  }, [surface, bareId, consented, threadShown]);

  const firstMessageSentRef = useRef(false);
  // CORE-1749 — второй аргумент от ленты — id сообщения, на которое отвечают.
  // В ИИ-канале тредов нет, и как родитель (`quotedParentId`) он делал бы ответ
  // репликой несуществующего обсуждения: цитата не доезжала бы до ИИ, а сервер
  // проверял бы её не тем правилом. Поэтому он уходит как `replyToMessageId`
  // (4-й аргумент), а выделенная фраза — рядом, 5-м.
  const trackedSend = useCallback(
    (text: string, replyToId?: string, attachments?: unknown, replyFragment?: string, clientMessageId?: string) => {
      const isFirst = !firstMessageSentRef.current && messages.length === 0;
      if (isFirst) {
        firstMessageSentRef.current = true;
        trackBehavior(BehaviorEvents.agapella.firstMessage, {
          surface,
          interest: interest.activity('agapella_chat'),
        });
      }
      trackBehavior(BehaviorEvents.agapella.topic, { surface });
      return (send as any)(text, undefined, attachments, replyToId, replyToId ? replyFragment : undefined, clientMessageId);
    },
    [send, messages.length, surface],
  );

  // Пока согласие не прочитано из хранилища — не показываем ни гейт, ни чат:
  // мигнувший на долю секунды экран согласия у того, кто его давно дал,
  // читается как «меня спрашивают заново».
  if (consentStatus === 'loading') return null;

  if (!consented) {
    if (declined) return <AiConsentDeclined onReadAgain={() => setDeclined(false)} />;
    return (
      <AiDataConsent
        appLanguage={i18n.language}
        onAccept={(lang) => accept(lang)}
        onDecline={() => {
          void revoke();
          if (onBack) onBack();
          else setDeclined(true);
        }}
      />
    );
  }

  // CORE-1401 — критерий 5: ссылка на Уведомление об ИИ в открытом чате.
  // Строка стоит под тредом целиком (то есть под полем ввода) и не трогает сам
  // тред: он общий с личными чатами и кругами, где ИИ нет.
  return (
    <View style={{ flex: 1 }}>
      <AgapellaChatThread
        messages={messages}
        thinking={thinking}
        loading={loading}
        error={error}
        peer={{
          name,
          avatarUrl,
          // Default assistant (Agapella) uses the branded local asset for her
          // message-row avatar, exactly like the header/sidebar do — otherwise the
          // backend-configured avatarUrl renders a different face in the bubble.
          brandedAvatar:
            entityType === 'ai' && companionId === DEFAULT_ASSISTANT_ID
              ? AGAPELLA_AI_ICON
              : undefined,
        }}
        onBack={onBack}
        headerSlot={headerSlot}
        onSend={trackedSend}
        onReact={react}
        onTyping={notifyTyping}
        // Без загрузчика от родителя композер остаётся текстовым, как был:
        // `aiUpload` всегда истинна, и передать её напрямую значило бы показать
        // скрепку там, где грузить некуда (виджет).
        onUpload={onUpload ? aiUpload : undefined}
        acceptFileTypes={acceptFileTypes}
        // CORE-1356 — скрепка без микрофона. До этой задачи один и тот же проп
        // включал оба, и строка, дающая вложения, молча включала бы запись
        // голоса: без дизайна и без слова человеку о том, что речь уезжает на
        // расшифровку. Решение продукта — голос в вебе пока выключен, и сказано
        // это здесь вслух, а не получается само собой.
        allowVoice={false}
        mentionables={entityType === 'wisdom-council' ? mentionables : undefined}
        // CORE-1749 — «Ответить» на сообщение Агапеллы (и своё) и «Спросить об
        // этом» у выделенной в её сообщении фразы.
        enableReply
        enableFragmentReply
      />
      <AiNoticeLink />
    </View>
  );
};
